import { addScales } from "@/components/PddContextProvider/DiceCanvases/helpers/addScales";
import { isDebug, WorkerPayloadTypes } from "@/constants";
import { Context } from "@/context";
import { isEmpty } from "lodash";
import { useContext, useEffect } from "react";

export const usePhysicsWorkerListeners = () => {
  const {
    manifests,
    logger,
    errorCallback,
    toastCallback,
    physicsWorker,
    setHasPhysicsWorkerErrored,
    setIsPhysicsWorkerReady,
    renderedWorker,
  } = useContext(Context);

  useEffect(() => {
    if (isEmpty(manifests) || !physicsWorker || !renderedWorker) return;

    if (isDebug) {
      console.log("Setting up physicsWorker listeners");
    }

    const handlePhysicsMessage = ({
      data: { type, payload },
    }: MessageEvent) => {
      if (type === WorkerPayloadTypes.ComponentMounted) {
        addScales(manifests, physicsWorker);
        setIsPhysicsWorkerReady?.(true);

        if (isDebug) {
          console.log("physicsWorker is ready");
        }
      } else if (type === WorkerPayloadTypes.ToastMessage) {
        toastCallback?.(payload);
      } else if (type === WorkerPayloadTypes.RenderDice) {
        renderedWorker.postMessage({
          type: WorkerPayloadTypes.RenderDice,
          payload,
        });
      } else if (type === WorkerPayloadTypes.Scales) {
        addScales(payload, physicsWorker);
      }
    };
    const handleError = (error: ErrorEvent) => {
      // clean up message handler if error happens before component mounts
      physicsWorker.removeEventListener("message", handlePhysicsMessage);

      if (isDebug) {
        logger?.error("physicsWorker error", {
          exception: JSON.stringify(error.message),
        });
      }

      physicsWorker.postMessage({
        type: WorkerPayloadTypes.RemoveEventListener,
      });
      errorCallback?.(
        "physicsWorker encountered an error. Falling back to instant dice.",
      );
      setHasPhysicsWorkerErrored?.(true);
    };

    physicsWorker.addEventListener("error", handleError);
    physicsWorker.addEventListener("message", handlePhysicsMessage);

    return () => {
      physicsWorker.removeEventListener("error", handleError);
      physicsWorker.removeEventListener("message", handlePhysicsMessage);
    };
  }, [
    errorCallback,
    logger,
    manifests,
    physicsWorker,
    renderedWorker,
    setHasPhysicsWorkerErrored,
    setIsPhysicsWorkerReady,
    toastCallback,
  ]);
};
